import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, RefreshCw, Unplug } from "lucide-react";
import {
  connectIntervals,
  disconnectIntervals,
  getIntervalsStatus,
  syncIntervals,
  type IntervalsStatus,
} from "@/api/integrations";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, SectionLabel } from "@/components/ui/Card";
import { relativeDistance } from "@/lib/dates";

export function IntervalsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const statusQ = useQuery<IntervalsStatus>({
    queryKey: ["intervals-status"],
    queryFn: getIntervalsStatus,
    refetchOnWindowFocus: true,
  });

  const [athleteId, setAthleteId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [connectError, setConnectError] = useState<string | null>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: () => connectIntervals(athleteId.trim(), apiKey.trim()),
    onSuccess: () => {
      setAthleteId("");
      setApiKey("");
      setConnectError(null);
      qc.invalidateQueries({ queryKey: ["intervals-status"] });
    },
    onError: (err: unknown) => {
      const e = err as {
        response?: { status?: number; data?: { detail?: string } };
      };
      if (e.response?.status === 401) {
        setConnectError(t("intervals_card.rejected_creds"));
      } else {
        setConnectError(
          e.response?.data?.detail || t("intervals_card.connect_failed"),
        );
      }
    },
  });

  const disconnect = useMutation({
    mutationFn: disconnectIntervals,
    onSuccess: () => {
      setSyncMsg(null);
      qc.invalidateQueries({ queryKey: ["intervals-status"] });
    },
  });

  const sync = useMutation({
    mutationFn: () => syncIntervals(30),
    onSuccess: (r) => {
      setSyncMsg(
        t("intervals_card.synced_msg", {
          new: r.new_workouts,
          updated: r.updated,
          total: r.total,
        }),
      );
      qc.invalidateQueries({ queryKey: ["intervals-status"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["history"] });
    },
    onError: () => setSyncMsg(t("intervals_card.sync_failed")),
  });

  function handleConnect(e: FormEvent) {
    e.preventDefault();
    setConnectError(null);
    if (!athleteId.trim() || !apiKey.trim()) {
      setConnectError(t("intervals_card.both_fields_required"));
      return;
    }
    connect.mutate();
  }

  const status = statusQ.data;

  if (statusQ.isLoading) {
    return (
      <Card>
        <SectionLabel>{t("intervals_card.section_label")}</SectionLabel>
        <p className="mt-2 text-[13px] text-taupe">{t("intervals_card.loading_status")}</p>
      </Card>
    );
  }

  if (!status || !status.connected) {
    return (
      <Card>
        <SectionLabel>{t("intervals_card.section_label")}</SectionLabel>
        <p className="mt-2 text-[13px] text-taupe">
          {t("intervals_card.connect_intro")}{" "}
          <a
            href="https://intervals.icu/settings"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-amber-dark hover:underline"
          >
            {t("intervals_card.connect_settings_link")}
          </a>{" "}
          {t("intervals_card.connect_intro_suffix")}
        </p>
        <form onSubmit={handleConnect} className="mt-3 space-y-3">
          <Input
            label={t("intervals_card.athlete_id_label")}
            placeholder="i12345"
            value={athleteId}
            onChange={(e) => setAthleteId(e.target.value)}
            autoComplete="off"
            required
          />
          <Input
            label={t("intervals_card.api_key_label")}
            type="password"
            placeholder="xxxxxxxxxxxxxxxx"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="off"
            required
          />
          {connectError ? (
            <div className="rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
              {connectError}
            </div>
          ) : null}
          <Button
            type="submit"
            size="md"
            loading={connect.isPending}
            className="w-full"
          >
            <Link2 className="h-4 w-4" /> {t("intervals_card.connect_button")}
          </Button>
          <p className="text-[11px] text-taupe">
            {t("intervals_card.fernet_note")}
          </p>
        </form>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{t("intervals_card.section_label")}</SectionLabel>
        <span className="rounded-full bg-amber/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-dark">
          ✓ {t("intervals_card.status_connected")}
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-[13px]">
        <div className="rounded-lg bg-cream px-3 py-2">
          <dt className="text-[11px] uppercase tracking-wider text-taupe">
            {t("intervals_card.athlete_id_label")}
          </dt>
          <dd className="metric-number text-[14px]">{status.athlete_id}</dd>
        </div>
        <div className="rounded-lg bg-cream px-3 py-2">
          <dt className="text-[11px] uppercase tracking-wider text-taupe">
            {t("intervals_card.last_sync_label")}
          </dt>
          <dd className="text-[13px] text-navy-deep">
            {status.last_synced_at ? relativeDistance(status.last_synced_at) : "—"}
          </dd>
        </div>
      </dl>
      {syncMsg ? (
        <div className="mt-3 rounded-lg bg-amber/10 px-3 py-2 text-[13px] text-amber-dark">
          {syncMsg}
        </div>
      ) : null}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button
          variant="secondary"
          size="md"
          onClick={() => sync.mutate()}
          loading={sync.isPending}
        >
          <RefreshCw className="h-4 w-4" /> {t("intervals_card.sync_button")}
        </Button>
        <Button
          variant="ghost"
          size="md"
          onClick={() => {
            if (confirm(t("intervals_card.disconnect_confirm"))) {
              disconnect.mutate();
            }
          }}
          loading={disconnect.isPending}
        >
          <Unplug className="h-4 w-4" /> {t("intervals_card.disconnect_button")}
        </Button>
      </div>
      <p className="mt-3 text-[11px] text-taupe">
        {t("intervals_card.auto_sync_note")}
      </p>
    </Card>
  );
}
